import { Check, ChevronDown } from 'lucide-react';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { fuzzyFilter } from '../lib/fuzzy';
import { Chip } from './Chip';
import { SearchInput } from './Input';
import { Popover, usePopover } from './Popover';
import styles from './Select.module.css';

// A button that opens an anchored Popover list. Never a raw <select> — a native
// dropdown is drawn by the operating system and ignores our palette, corners,
// shadow and motion entirely (CLAUDE.md §5).

export interface SelectOption {
  readonly value: string;
  readonly label: string;
  readonly description?: string;
  readonly disabled?: boolean;
  /** A short mark beside the label, e.g. "Certified" on a recommended template (F5.23). */
  readonly badge?: string;
  /** What the filter box matches instead of the label, e.g. "3:45 pm 15:45" for a time. */
  readonly searchText?: string;
}

const filterText = (option: SelectOption): string => option.searchText ?? option.label;

/** Longer than this, the list gets its own filter box rather than a long scroll. */
const FILTER_THRESHOLD = 8;

interface SelectProps {
  readonly options: readonly SelectOption[];
  readonly value: string | null;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly isInvalid?: boolean;
  readonly disabled?: boolean;
  readonly id?: string;
  readonly describedBy?: string;
  readonly label: string;
  /** Turns the filter text into an option, e.g. a typed 10:06 or "3:45p" in the time picker. One the list
   *  doesn't have is offered as "Use …"; one it has goes to the top. Enter takes either. */
  readonly typedOption?: (query: string) => SelectOption | null;
}

export function Select({
  options,
  value,
  onChange,
  placeholder = 'Select',
  isInvalid = false,
  disabled = false,
  id,
  describedBy,
  label,
  typedOption,
}: SelectProps): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const [query, setQuery] = useState('');

  const selected = options.find((option) => option.value === value) ?? null;
  const typedValue = useMemo(() => typedOption?.(query) ?? null, [typedOption, query]);
  const listed = typedValue ? (options.find((existing) => existing.value === typedValue.value) ?? null) : null;
  const typed = typedValue && !listed ? typedValue : null;
  const filtered = useMemo(() => {
    const matches = fuzzyFilter(query, options, filterText).map((result) => result.item);
    if (listed) return [listed, ...matches.filter((option) => option !== listed)];
    return typed ? [typed, ...matches] : matches;
  }, [query, options, typed, listed]);

  const choose = (option: SelectOption): void => {
    onChange(option.value);
    setQuery('');
    close();
  };

  return (
    <>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className={`${styles.trigger} ${isInvalid ? styles.invalid : ''}`}
        onClick={toggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-describedby={describedBy}
        aria-invalid={isInvalid || undefined}
      >
        <span className={`${styles.value} ${selected ? '' : styles.placeholder}`}>
          {selected?.label ?? placeholder}
        </span>
        <span className={styles.chevron} aria-hidden>
          <ChevronDown size={16} />
        </span>
      </button>

      <Popover anchor={anchor} onClose={close} label={label}>
        {options.length > FILTER_THRESHOLD && (
          <div className={styles.filterRow}>
            <SearchInput
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                const pick = typed ?? listed;
                if (event.key !== 'Enter' || !pick) return;
                event.preventDefault();
                choose(pick);
              }}
              placeholder="Filter"
              autoFocus
            />
          </div>
        )}
        <div role="listbox" aria-label={label}>
          {filtered.length === 0 && <p className={styles.noOptions}>No matches</p>}
          {filtered.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={styles.option}
              disabled={option.disabled}
              onClick={() => choose(option)}
            >
              <span className={styles.optionText}>
                {option === typed ? `Use ${option.label}` : option.label}
                {option.description !== undefined && (
                  <span className={styles.optionDescription}>{option.description}</span>
                )}
              </span>
              {option.badge !== undefined && <Chip tone="success">{option.badge}</Chip>}
              {option.value === value && (
                <span className={styles.optionTick} aria-hidden>
                  <Check size={15} />
                </span>
              )}
            </button>
          ))}
        </div>
      </Popover>
    </>
  );
}

interface MultiSelectProps extends Omit<SelectProps, 'value' | 'onChange'> {
  readonly value: readonly string[];
  readonly onChange: (value: readonly string[]) => void;
}

/**
 * Chosen values appear as removable chips inside the trigger, which grows to fit
 * rather than truncating — a multi-select that hides what is selected is one
 * nobody trusts.
 */
export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Select',
  isInvalid = false,
  disabled = false,
  id,
  describedBy,
  label,
}: MultiSelectProps): ReactNode {
  // R9: a plain box holds the chips (each with its own remove button) and one real button that opens
  // the list — a button may not hold another, and a `role="button"` would hide the chips' buttons from
  // a screen reader. The box is the anchor; a click on its empty space opens the list too.
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover<HTMLDivElement>();
  const openerRef = useRef<HTMLButtonElement | null>(null);
  const [query, setQuery] = useState('');

  const selectedSet = new Set(value);
  const filtered = useMemo(
    () => fuzzyFilter(query, options, filterText).map((result) => result.item),
    [query, options],
  );

  const toggleValue = (optionValue: string): void => {
    onChange(
      selectedSet.has(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue],
    );
  };
  const closeList = (): void => {
    close();
    // Focus returns to the control that opened it (06-qol §2).
    openerRef.current?.focus();
  };
  const summary = value.length === 0 ? placeholder : `${value.length} chosen`;

  return (
    <>
      <div
        ref={triggerRef}
        className={`${styles.trigger} ${styles.multi} ${isInvalid ? styles.invalid : ''}`}
        aria-disabled={disabled || undefined}
        onClick={(event) => {
          if (disabled || (event.target instanceof Element && event.target.closest('button'))) return;
          toggle();
        }}
      >
        {value.length > 0 && (
          <span className={styles.chips}>
            {value.map((selectedValue) => {
              const option = options.find((o) => o.value === selectedValue);
              return (
                <Chip
                  key={selectedValue}
                  plain
                  onRemove={disabled ? undefined : () => toggleValue(selectedValue)}
                  removeLabel={`Remove ${option?.label ?? selectedValue}`}
                >
                  {option?.label ?? selectedValue}
                </Chip>
              );
            })}
          </span>
        )}
        <button
          ref={openerRef}
          id={id}
          type="button"
          className={styles.opener}
          onClick={toggle}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label={`${label}: ${summary}`}
          aria-describedby={describedBy}
        >
          {value.length === 0 && <span className={`${styles.value} ${styles.placeholder}`}>{placeholder}</span>}
          <span className={styles.chevron} aria-hidden>
            <ChevronDown size={16} />
          </span>
        </button>
      </div>

      <Popover anchor={anchor} onClose={closeList} label={label}>
        {options.length > FILTER_THRESHOLD && (
          <div className={styles.filterRow}>
            <SearchInput
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Filter"
              autoFocus
            />
          </div>
        )}
        <div role="listbox" aria-multiselectable="true" aria-label={label}>
          {filtered.length === 0 && <p className={styles.noOptions}>No matches</p>}
          {filtered.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={selectedSet.has(option.value)}
              className={styles.option}
              disabled={option.disabled}
              onClick={() => toggleValue(option.value)}
            >
              <span className={styles.optionText}>
                {option.label}
                {option.description !== undefined && (
                  <span className={styles.optionDescription}>{option.description}</span>
                )}
              </span>
              {selectedSet.has(option.value) && (
                <span className={styles.optionTick} aria-hidden>
                  <Check size={15} />
                </span>
              )}
            </button>
          ))}
        </div>
      </Popover>
    </>
  );
}
